import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../../components/AnimatedSection';

export default function SolutionsPage() {
  const { t } = useTranslation();

  const solutions = [
    {
      icon: 'ri-layout-grid-line',
      title: t('sol_s1_title'),
      desc: t('sol_s1_desc'),
      features: [t('sol_s1_f1'), t('sol_s1_f2'), t('sol_s1_f3'), t('sol_s1_f4')],
    },
    {
      icon: 'ri-cloud-line',
      title: t('sol_s2_title'),
      desc: t('sol_s2_desc'),
      features: [t('sol_s2_f1'), t('sol_s2_f2'), t('sol_s2_f3'), t('sol_s2_f4')],
    },
    {
      icon: 'ri-smartphone-line',
      title: t('sol_s3_title'),
      desc: t('sol_s3_desc'),
      features: [t('sol_s3_f1'), t('sol_s3_f2'), t('sol_s3_f3'), t('sol_s3_f4')],
    },
    {
      icon: 'ri-links-line',
      title: t('sol_s4_title'),
      desc: t('sol_s4_desc'),
      features: [t('sol_s4_f1'), t('sol_s4_f2'), t('sol_s4_f3'), t('sol_s4_f4')],
    },
  ];

  const process = [
    { step: t('sol_p1_step'), title: t('sol_p1_title'), desc: t('sol_p1_desc') },
    { step: t('sol_p2_step'), title: t('sol_p2_title'), desc: t('sol_p2_desc') },
    { step: t('sol_p3_step'), title: t('sol_p3_title'), desc: t('sol_p3_desc') },
    { step: t('sol_p4_step'), title: t('sol_p4_title'), desc: t('sol_p4_desc') },
    { step: t('sol_p5_step'), title: t('sol_p5_title'), desc: t('sol_p5_desc') },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <img
            src="https://public.readdy.ai/ai/img_res/b6c408b8de7fb9c16fe1d8a60e5fae82.jpg"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-950"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <AnimatedSection animation="fade-in">
            <nav className="flex items-center gap-2 text-slate-400 text-sm mb-8">
              <Link to="/" className="hover:text-cyan-400 cursor-pointer">{t('sol_breadcrumb_home')}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">{t('sol_breadcrumb_current')}</span>
            </nav>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
              <i className="ri-code-s-slash-line text-cyan-400 text-sm"></i>
              <span className="text-cyan-400 text-sm font-medium">{t('sol_badge')}</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
              {t('sol_title1')}<br />
              <span className="text-cyan-400">{t('sol_title2')}</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl leading-relaxed mb-8">
              {t('sol_desc')}
            </p>
            <div className="flex gap-4">
              <Link to="/contact" className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold px-6 py-3 rounded-lg transition-all whitespace-nowrap cursor-pointer">
                {t('sol_cta1')}
              </Link>
              <Link to="/case-studies" className="border border-slate-600 hover:border-cyan-500 text-white hover:text-cyan-400 px-6 py-3 rounded-lg transition-all whitespace-nowrap cursor-pointer">
                {t('sol_cta2')}
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection animation="fade-in">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-white mb-3">{t('sol_grid_title')}</h2>
              <p className="text-slate-400">{t('sol_grid_sub')}</p>
            </div>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((sol, i) => (
              <AnimatedSection key={sol.title} animation="fade-in" delay={i * 0.1}>
                <div className="bg-slate-800/50 border border-slate-700 hover:border-cyan-500/40 rounded-2xl p-8 transition-all">
                  <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/10 rounded-xl mb-5">
                    <i className={`${sol.icon} text-cyan-400 text-xl`}></i>
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{sol.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-5">{sol.desc}</p>
                  <ul className="grid grid-cols-2 gap-2">
                    {sol.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-slate-300 text-xs">
                        <i className="ri-checkbox-circle-line text-cyan-500"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection animation="fade-in">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-white mb-3">{t('sol_process_title')}</h2>
              <p className="text-slate-400">{t('sol_process_sub')}</p>
            </div>
          </AnimatedSection>
          <div className="flex flex-col md:flex-row gap-0">
            {process.map((p, i) => (
              <AnimatedSection key={p.step} animation="fade-in" delay={i * 0.1}>
                <div className="flex-1 relative flex flex-col items-center text-center px-4 pb-8 md:pb-0">
                  {i < process.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-px bg-gradient-to-r from-cyan-500/50 to-transparent"></div>
                  )}
                  <div className="w-16 h-16 flex items-center justify-center bg-slate-800 border-2 border-cyan-500/50 rounded-full text-cyan-400 font-bold text-lg mb-4 relative z-10">
                    {p.step}
                  </div>
                  <h4 className="text-white font-semibold mb-2">{p.title}</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900 border-t border-slate-800">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t('sol_cta_title')}</h2>
          <p className="text-slate-400 mb-8">{t('sol_cta_desc')}</p>
          <Link to="/contact" className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold px-8 py-4 rounded-lg transition-all whitespace-nowrap cursor-pointer">
            {t('sol_cta_btn')}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
